import { useAPIQuery } from "../../../../hooks/useAPIQuery";
import { useEffect, useMemo, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

const formatCompactPlanDate = (dateStr) => {
    if (!dateStr) return "";
    const parsed = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
    });
};

const buildBatchSelectOption = (batch) => {
    const returnable =
        batch.returnable_quantity ?? batch.allotted_quantity ?? 0;
    const dateSuffix =
        batch.type === "DP" && batch.plan_date
            ? ` · ${formatCompactPlanDate(batch.plan_date)}`
            : "";

    return {
        value: batch.batch_id,
        label: `${batch.batch_number}${dateSuffix} (returnable: ${returnable})`,
        allotted_quantity: returnable,
        type: batch.type,
    };
};

const getDefaultBatchSelectOption = (batches) => {
    const options = (batches ?? []).map(buildBatchSelectOption);
    return (
        options.find((option) => Number(option.allotted_quantity || 0) > 0) ??
        options[0] ??
        null
    );
};

const ReturnDailyPlanMaterialModal = ({ productionData }) => {
    const navigate = useNavigate();
    const closeBtnRef = useRef(null);

    const handleCloseModal = () => {
        if (closeBtnRef.current) {
            closeBtnRef.current.click();
            navigate(-1);
        }
    };

    const { useFetch: useFetchRetrunSlip, useCreate: useCreateReturnSlip } =
        useAPIQuery({
            key: "api/return/slip",
            endpoint: "/api/return/slip/",
        });

    const { data: retrunSlip, isLoading: loadingReturnSlip } =
        useFetchRetrunSlip({
            params: { daily_plan_id: productionData?.id },
            enabled: !!productionData,
        });

    const productionContext = useMemo(
        () => retrunSlip?.production_context || {},
        [retrunSlip]
    );

    const { mutate: createReturnSlip, isPending } = useCreateReturnSlip({
        successMessageFromServer: true,
        errorMessageFromServer: true,
        onSuccess: () => {
            handleCloseModal();
        },
    });

    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        reset,
        watch,
        getValues,
        setValue,
    } = useForm({
        mode: "onBlur",
        defaultValues: {
            all_material_consumed: false,
            return: [],
        },
    });

    const { fields, insert, remove } = useFieldArray({
        control,
        name: "return",
    });

    const watchReturn = watch("return") || [];
    const watchAllMaterialConsumed = watch("all_material_consumed");

    const totalReturnQty = useMemo(
        () =>
            (watchReturn || []).reduce(
                (sum, row) => sum + Number(row?.remaining_quantity || 0),
                0
            ),
        [watchReturn]
    );

    const totalPendingQty = useMemo(
        () =>
            (watchReturn || []).reduce(
                (sum, row) => sum + Number(row?.max_remaining_quantity || 0),
                0
            ),
        [watchReturn]
    );

    const onSubmit = (data) => {
        const res =
            data?.return
                ?.map((r) => {
                    const raw = Number(r?.remaining_quantity || 0);
                    // Ledger min precision is 0.0001; drop float dust like 3e-05.
                    const return_quantity = Math.round(raw * 10000) / 10000;
                    return {
                        requisition_id: r?.requisition_id,
                        batch_id: r?.batch?.value ?? null,
                        return_quantity,
                    };
                })
                ?.filter((r) => r.return_quantity >= 0.0001) ?? [];

        const needsAck =
            productionContext?.requires_all_material_consumed &&
            totalPendingQty > 0 &&
            res.length === 0;

        if (needsAck && !data.all_material_consumed) {
            Swal.fire({
                icon: "warning",
                title: "Confirmation required",
                text: 'Check "All material consumed" to close with zero material return, or enter return quantities.',
            });
            return;
        }

        const confirmText =
            needsAck && data.all_material_consumed
                ? "Close this daily plan confirming all material was consumed (zero return)?"
                : productionContext?.mode === "surplus" &&
                    !productionContext?.within_threshold
                  ? "Surplus production is above threshold. Extra unissued material will be recorded. Continue?"
                  : "Do you want to return this material / close the daily plan?";

        Swal.fire({
            title: "Are you sure?",
            text: confirmText,
            icon: "warning",
            confirmButtonText: "Yes",
            denyButtonText: `No, cancel!`,
            showDenyButton: true,
        }).then((result) => {
            if (result.isConfirmed) {
                createReturnSlip({
                    data: {
                        daily_plan_id: productionData?.id,
                        all_material_consumed: Boolean(
                            data.all_material_consumed
                        ),
                        ...(res.length ? { returns: res } : {}),
                    },
                });
            }
        });
    };

    useEffect(() => {
        if (retrunSlip?.data) {
            reset({
                all_material_consumed: false,
                return: retrunSlip.data.map((rs) => {
                    const maxRemaining = rs.remaining_quantity ?? 0;
                    const defaultBatch = getDefaultBatchSelectOption(rs.batches);
                    const remainingQuantity =
                        defaultBatch && maxRemaining > 0
                            ? Math.min(
                                  maxRemaining,
                                  Number(defaultBatch.allotted_quantity || 0)
                              )
                            : 0;

                    return {
                        item_id: rs.item_id,
                        item_name: rs.item_name,
                        received_quantity: rs.received_quantity,
                        utilized_quantity: rs.utilized_quantity,
                        shortage_quantity: rs.shortage_quantity,
                        is_surplus_production: rs.is_surplus_production,
                        production_material_variance:
                            rs.production_material_variance,
                        requisition_id: rs.requisition_id,
                        batches: rs.batches,
                        max_remaining_quantity: maxRemaining,
                        remaining_quantity: remainingQuantity,
                        batch: defaultBatch,
                    };
                }),
            });
        }
    }, [retrunSlip, reset]);

    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style.cssText = "";
            }
        };
    }, []);

    const getGroupRows = (index) => {
        const all = watchReturn;
        const current = all?.[index];
        if (!current) return [];

        return all
            .map((row, idx) => ({ ...row, _idx: idx }))
            .filter((row) => row.requisition_id === current.requisition_id);
    };

    const handleAddRowBelow = (index) => {
        const currentRow = getValues(`return.${index}`);
        if (!currentRow) return;

        const newRow = {
            ...currentRow,
            batch: null,
            remaining_quantity: 0,
        };

        insert(index + 1, newRow);
    };

    const handleRemoveRow = (index) => {
        remove(index);
    };

    return (
        <div
            className="modal fade m-0 p-0"
            tabIndex={-1}
            id="retrun-dp-material-modal"
            aria-modal
        >
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content">
                    <button
                        type="button"
                        className="close"
                        data-bs-dismiss="modal"
                        ref={closeBtnRef}
                    >
                        <em
                            className="icon ni ni-cross"
                            aria-hidden="true"
                        ></em>
                    </button>
                    <div className="modal-header">
                        <h5 className="modal-title">Close daily plan</h5>
                    </div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="modal-body">
                            {productionContext?.mode && (
                                <div
                                    className={`alert py-2 ${
                                        productionContext.mode ===
                                        "underproduction"
                                            ? "alert-warning"
                                            : productionContext.mode ===
                                                "surplus"
                                              ? "alert-info"
                                              : "alert-secondary"
                                    }`}
                                >
                                    <div className="fw-bold text-capitalize">
                                        Mode:{" "}
                                        {String(
                                            productionContext.mode
                                        ).replace("_", " ")}
                                    </div>
                                    <div className="small">
                                        Original qty:{" "}
                                        {productionContext.original_qty} | Updated:{" "}
                                        {productionContext.updated_qty} | Actual:{" "}
                                        {productionContext.actual_qty}
                                        {productionContext.mode ===
                                            "surplus" && (
                                            <>
                                                {" "}
                                                | Surplus:{" "}
                                                {
                                                    productionContext.surplus_percent
                                                }
                                                % (threshold{" "}
                                                {
                                                    productionContext.threshold_percent
                                                }
                                                %)
                                                {!productionContext.within_threshold &&
                                                    " — Extra unissued material will be recorded for reporting."}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {productionContext?.requires_all_material_consumed &&
                                totalPendingQty > 0 &&
                                totalReturnQty <= 0 && (
                                    <div className="form-check mb-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            id="all-material-consumed"
                                            {...register(
                                                "all_material_consumed"
                                            )}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="all-material-consumed"
                                        >
                                            All material consumed
                                        </label>
                                        <div className="form-text">
                                            Allow closing with zero material
                                            return when produced quantity is
                                            less than original plan.
                                            {!watchAllMaterialConsumed && (
                                                <span className="text-danger">
                                                    {" "}
                                                    Required if you return
                                                    nothing.
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}

                            <div className="table-responsive">
                                <table className="table table-sm align-middle">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "20%" }}>
                                                Item
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Received
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Utilized
                                            </th>
                                            <th
                                                style={{ width: "16%" }}
                                                className="text-end"
                                            >
                                                Batches
                                            </th>
                                            <th
                                                style={{ width: "14%" }}
                                                className="text-end"
                                            >
                                                Remaining
                                            </th>
                                            <th
                                                style={{ width: "15%" }}
                                                className="text-end"
                                            >
                                                Variance
                                            </th>
                                            <th
                                                style={{ width: "10%" }}
                                                className="text-center"
                                            >
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {fields.map((r, index) => {
                                            const groupRows =
                                                getGroupRows(index);
                                            const allBatchesForMaterial =
                                                r?.batches ?? [];
                                            const disableRemoveButton =
                                                groupRows.length === 1;
                                            const maxRemaining =
                                                r?.max_remaining_quantity ??
                                                r?.remaining_quantity ??
                                                0;

                                            const selectedBatchIdsInGroup =
                                                groupRows
                                                    .filter(
                                                        (row) =>
                                                            row._idx !==
                                                                index &&
                                                            row?.batch?.value !=
                                                                null
                                                    )
                                                    .map(
                                                        (row) => row.batch.value
                                                    );

                                            const batchOptions =
                                                allBatchesForMaterial
                                                    .filter(
                                                        (b) =>
                                                            !selectedBatchIdsInGroup.includes(
                                                                b.batch_id
                                                            )
                                                    )
                                                    .map(buildBatchSelectOption) ??
                                                [];

                                            const allBatchIdsForMaterial =
                                                allBatchesForMaterial.map(
                                                    (b) => b.batch_id
                                                );
                                            const selectedBatchIdsForMaterial =
                                                groupRows
                                                    .map(
                                                        (row) =>
                                                            row?.batch?.value
                                                    )
                                                    .filter(Boolean);

                                            const allBatchesSelected =
                                                allBatchIdsForMaterial.length >
                                                    0 &&
                                                allBatchIdsForMaterial.every(
                                                    (id) =>
                                                        selectedBatchIdsForMaterial.includes(
                                                            id
                                                        )
                                                );

                                            const disableAddButton =
                                                maxRemaining <= 0 ||
                                                allBatchesSelected ||
                                                allBatchesForMaterial.length ===
                                                    0;

                                            return (
                                                <tr key={r.id}>
                                                    <td>{r.item_name}</td>

                                                    <td className="text-end">
                                                        {r.received_quantity}
                                                    </td>

                                                    <td className="text-end">
                                                        {r.utilized_quantity}
                                                    </td>

                                                    <td className="text-end">
                                                        <Controller
                                                            name={`return.${index}.batch`}
                                                            rules={{
                                                                validate: (
                                                                    value
                                                                ) =>
                                                                    maxRemaining <=
                                                                        0 ||
                                                                    !!value ||
                                                                    "Batch is required",
                                                            }}
                                                            control={control}
                                                            render={({
                                                                field,
                                                            }) => (
                                                                <>
                                                                    <Select
                                                                        {...field}
                                                                        options={
                                                                            batchOptions
                                                                        }
                                                                        isLoading={
                                                                            loadingReturnSlip
                                                                        }
                                                                        isClearable
                                                                        styles={{
                                                                            menuPortal:
                                                                                (
                                                                                    base
                                                                                ) => ({
                                                                                    ...base,
                                                                                    zIndex: 9999,
                                                                                }),
                                                                            menu: (
                                                                                base
                                                                            ) => ({
                                                                                ...base,
                                                                                zIndex: 9999,
                                                                            }),
                                                                        }}
                                                                        menuPortalTarget={
                                                                            document.body
                                                                        }
                                                                        placeholder="Select a batch"
                                                                        menuPlacement="auto"
                                                                        menuPosition="fixed"
                                                                        isDisabled={
                                                                            maxRemaining <=
                                                                            0
                                                                        }
                                                                        onChange={(
                                                                            selected
                                                                        ) => {
                                                                            field.onChange(
                                                                                selected
                                                                            );

                                                                            const currentValues =
                                                                                getValues(
                                                                                    "return"
                                                                                ) ||
                                                                                [];
                                                                            const currentRow =
                                                                                currentValues[
                                                                                    index
                                                                                ];

                                                                            if (
                                                                                selected
                                                                            ) {
                                                                                const batchMax =
                                                                                    Math.min(
                                                                                        maxRemaining,
                                                                                        Number(
                                                                                            selected?.allotted_quantity ||
                                                                                                0
                                                                                        )
                                                                                    );

                                                                                const group =
                                                                                    getGroupRows(
                                                                                        index
                                                                                    );
                                                                                const otherTotal =
                                                                                    group
                                                                                        .filter(
                                                                                            (
                                                                                                r
                                                                                            ) =>
                                                                                                r._idx !==
                                                                                                index
                                                                                        )
                                                                                        .reduce(
                                                                                            (
                                                                                                sum,
                                                                                                r
                                                                                            ) =>
                                                                                                sum +
                                                                                                Number(
                                                                                                    r.remaining_quantity ||
                                                                                                        0
                                                                                                ),
                                                                                            0
                                                                                        );

                                                                                // Max allowed AFTER subtracting other rows
                                                                                const autoMax =
                                                                                    Math.min(
                                                                                        batchMax,
                                                                                        maxRemaining -
                                                                                            otherTotal
                                                                                    );

                                                                                const currentQty =
                                                                                    Number(
                                                                                        currentRow?.remaining_quantity ||
                                                                                            0
                                                                                    );

                                                                                if (
                                                                                    !currentQty ||
                                                                                    currentQty ===
                                                                                        0
                                                                                ) {
                                                                                    setValue(
                                                                                        `return.${index}.remaining_quantity`,
                                                                                        autoMax
                                                                                    );
                                                                                }
                                                                            } else {
                                                                                setValue(
                                                                                    `return.${index}.remaining_quantity`,
                                                                                    0
                                                                                );
                                                                            }
                                                                        }}
                                                                    />
                                                                    {errors
                                                                        ?.return?.[
                                                                        index
                                                                    ]?.batch
                                                                        ?.message && (
                                                                        <span className="fs-8 text-danger">
                                                                            {
                                                                                errors
                                                                                    ?.return?.[
                                                                                    index
                                                                                ]
                                                                                    ?.batch
                                                                                    ?.message
                                                                            }
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        />
                                                    </td>

                                                    <td className="text-end">
                                                        <input
                                                            type="number"
                                                            step="0.00001"
                                                            disabled={
                                                                maxRemaining <=
                                                                0
                                                            }
                                                            className="form-control form-control-sm text-end"
                                                            style={{
                                                                minWidth:
                                                                    "110px",
                                                            }}
                                                            {...register(
                                                                `return.${index}.remaining_quantity`,
                                                                {
                                                                    valueAsNumber: true,
                                                                    disabled:
                                                                        maxRemaining <=
                                                                        0,

                                                                    validate: (
                                                                        value
                                                                    ) => {
                                                                        const num =
                                                                            Number(
                                                                                value ||
                                                                                    0
                                                                            );
                                                                        if (
                                                                            num <
                                                                            0
                                                                        )
                                                                            return "Minimum allowed is 0";

                                                                        const selected =
                                                                            getValues(
                                                                                `return.${index}.batch`
                                                                            );

                                                                        // ⬅️ Per-batch maximum limit
                                                                        const batchMax =
                                                                            Math.min(
                                                                                maxRemaining,
                                                                                Number(
                                                                                    selected?.allotted_quantity ||
                                                                                        0
                                                                                )
                                                                            );

                                                                        if (
                                                                            num >
                                                                            batchMax
                                                                        ) {
                                                                            return `Max allowed for this batch is ${batchMax}`;
                                                                        }

                                                                        // ⬅️ Combined total validation
                                                                        const group =
                                                                            getGroupRows(
                                                                                index
                                                                            );
                                                                        const otherTotal =
                                                                            group
                                                                                .filter(
                                                                                    (
                                                                                        r
                                                                                    ) =>
                                                                                        r._idx !==
                                                                                        index
                                                                                )
                                                                                .reduce(
                                                                                    (
                                                                                        sum,
                                                                                        r
                                                                                    ) =>
                                                                                        sum +
                                                                                        Number(
                                                                                            r.remaining_quantity ||
                                                                                                0
                                                                                        ),
                                                                                    0
                                                                                );

                                                                        if (
                                                                            num +
                                                                                otherTotal >
                                                                            maxRemaining
                                                                        ) {
                                                                            return `Total for this material cannot exceed ${maxRemaining}`;
                                                                        }

                                                                        return true;
                                                                    },
                                                                }
                                                            )}
                                                        />

                                                        {errors?.return?.[index]
                                                            ?.remaining_quantity && (
                                                            <small className="text-danger d-block text-start">
                                                                {
                                                                    errors
                                                                        .return[
                                                                        index
                                                                    ]
                                                                        .remaining_quantity
                                                                        .message
                                                                }
                                                            </small>
                                                        )}
                                                    </td>

                                                    <td className="text-end">
                                                        {r.is_surplus_production &&
                                                        r.production_material_variance ? (
                                                            <span className="badge bg-warning text-dark text-wrap">
                                                                Extra:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .surplus_production_quantity
                                                                }
                                                                <br />
                                                                Mat:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .surplus_required_quantity
                                                                }
                                                                <br />
                                                                Short:{" "}
                                                                {
                                                                    r
                                                                        .production_material_variance
                                                                        .shortage_quantity
                                                                }
                                                            </span>
                                                        ) : (
                                                            "-"
                                                        )}
                                                    </td>

                                                    <td className="text-center">
                                                        <div className="btn-group btn-group-sm">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary"
                                                                onClick={() =>
                                                                    handleAddRowBelow(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={
                                                                    disableAddButton
                                                                }
                                                                title={
                                                                    disableAddButton
                                                                        ? "All batches used"
                                                                        : "Add another batch row"
                                                                }
                                                            >
                                                                +
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-danger"
                                                                onClick={() =>
                                                                    handleRemoveRow(
                                                                        index
                                                                    )
                                                                }
                                                                disabled={
                                                                    disableRemoveButton
                                                                }
                                                                title={
                                                                    disableRemoveButton
                                                                        ? "At least one row must remain"
                                                                        : "Remove this row"
                                                                }
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="modal-footer bg-light">
                            <button
                                type="button"
                                className="btn btn-danger"
                                data-bs-dismiss="modal"
                                disabled={isPending}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={isPending}
                            >
                                {isPending ? "Submitting" : "Submit"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReturnDailyPlanMaterialModal;
